import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Последний рубеж: исключение при отрисовке.
 *
 * Оно здесь ожидаемо и осознанно. Домен приложения падает намеренно там, где данные
 * не сходятся, — неизвестная отрасль (`presetFor`), незаполненный плейсхолдер (`fill`),
 * негодное окно отмены (`safeCancelDeadline`). Это правило 7 из AGENTS.md: тихий фолбэк
 * хуже отказа. Но React без границы ошибок размонтирует всё дерево, и «видимой ошибкой»
 * оказывается белый экран, по которому нельзя понять ровным счётом ничего.
 *
 * Граница превращает падение в читаемое сообщение с текстом исключения — и в запись
 * в консоли клиента. Ни одна ветка здесь не восстанавливает работу «как будто ничего
 * не было»: перезапуск делает человек, увидев, что именно сломалось.
 */
interface State {
  readonly error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Сообщение в консоль — единственный след, который останется у поддержки:
    // серверных логов у отрисовки нет.
    console.error('Экран упал при отрисовке:', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    // Текст намеренно НЕ переводится: провал мог случиться и в самом словаре, и попытка
    // перевести сообщение об ошибке уронила бы границу ошибок повторно.
    return (
      <div className="flex h-full flex-col justify-center gap-3 bg-bg px-6 text-ink">
        <div className="text-[17px] font-extrabold">Приложение остановилось</div>
        <div className="text-[13px] leading-[1.5] text-ink-2">{error.message}</div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-2 w-full rounded-[14px] bg-accent px-4 py-3.5 text-[15px] font-bold text-on-accent"
        >
          Перезапустить · Qayta ishga tushirish
        </button>
      </div>
    );
  }
}
